import React from 'react';

import { Box, Typography } from '@mui/material';

import { Field, Form } from '@hrlogics/react-components/form';
import { validateWithYupSchema } from '@hrlogics/react-form-utils/yup';

import {
  useGetVerifierByEmailDomainLazyQuery,
  useVerifyEmailConfirmationCodeLazyQuery,
} from 'src/api';
import { Button } from 'src/components/common';
import { TextField } from 'src/components/finalForm';
import { SignupVerifierFormStepId } from 'src/constants/auth';
import { useNotification } from 'src/hooks/app';
import { useAuthSignupVerifier } from 'src/hooks/auth';

import { EmailConfirmationFormFields, EmailConfirmationFormValues } from '../types';
import { emailConfirmationFormFieldsValidationSchema } from '../utils';

export const EmailConfirmationForm: React.FC = () => {
  const { createApolloContext } = useNotification();

  const { formData, updateFormData, updateFormRelatedPage, goToStep } = useAuthSignupVerifier();

  const [verifyEmailConfirmationCode] = useVerifyEmailConfirmationCodeLazyQuery({
    fetchPolicy: 'network-only',
    context: createApolloContext(),
  });
  const [getVerifierByEmailDomain] = useGetVerifierByEmailDomainLazyQuery({
    fetchPolicy: 'network-only',
  });

  const userEmail = React.useMemo(() => String(formData.email), [formData.email]);

  const handleSubmit = React.useCallback(
    async (formValues: EmailConfirmationFormValues) => {
      const code = formValues.emailConfirmationCode;

      try {
        const { data: verificationResult, error } = await verifyEmailConfirmationCode({
          variables: { email: userEmail, code },
        });

        if (error?.message) throw new Error(error.message);

        if (verificationResult?.verifyEmailConfirmationCode.success) {
          const { data } = await getVerifierByEmailDomain({
            variables: {
              email: userEmail,
              emailConfirmationCode: code,
            },
          });

          const verifierByEmailDomain = data?.getVerifierByEmailDomain;

          if (data?.getVerifierByEmailDomain.verifiers) {
            updateFormRelatedPage(verifierByEmailDomain);
            updateFormData(formValues);
            goToStep(SignupVerifierFormStepId.relatedCompany);
          } else {
            updateFormData(formValues);
            goToStep(SignupVerifierFormStepId.choseVerifierAccountType);
          }
        } else {
          throw new Error('Unknown error');
        }
      } catch (error) {
        console.error(error);
      }
    },
    [
      getVerifierByEmailDomain,
      goToStep,
      updateFormData,
      updateFormRelatedPage,
      userEmail,
      verifyEmailConfirmationCode,
    ],
  );

  return (
    <React.Fragment>
      <Typography variant="h5">Check your Email</Typography>

      <Typography variant="body1" color="text.secondary" mt={1}>
        We have sent the verification code to{' '}
        <Typography component="strong" variant="body1" fontWeight={500}>
          {userEmail}
        </Typography>
        . Type it below to confirm your Email address.
      </Typography>

      <Form<EmailConfirmationFormValues>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        subscription={{ valid: true, submitting: true }}
        containerProps={{ mt: 4 }}
        validate={validateWithYupSchema(emailConfirmationFormFieldsValidationSchema)}
      >
        {({ submitting, valid }) => (
          <React.Fragment>
            <Box>
              <Field
                component={TextField}
                name={EmailConfirmationFormFields.emailConfirmationCode}
                label="Code"
                slotProps={{ input: { autoComplete: 'off' } }}
                fullWidth
                required
                disabled={submitting}
              />
            </Box>

            <Box display="flex" justifyContent="center" mt={4}>
              <Button
                type="submit"
                disabled={submitting || !valid}
                loading={submitting}
                color="primary"
                size="large"
                variant="contained"
              >
                Submit
              </Button>
            </Box>
          </React.Fragment>
        )}
      </Form>
    </React.Fragment>
  );
};

const initialValues: Partial<EmailConfirmationFormValues> = {};
